import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subject, takeUntil } from "rxjs";
import { Functionality } from "src/app/shared/constants/functionality.constants";
import { PermissionService } from "src/app/shared/services/permission.service";
import { SessionService } from "src/app/shared/services/session.service";
import { IExposedOrganizationOverview, IOrganizationDetails } from "../../domain/interfaces/organization.interface";
import { OrganizationService } from "../../domain/services/organization.service";

@Component({
  selector: "app-administration-page",
  templateUrl: "./administration-page.component.html",
  styleUrls: ["./administration-page.component.scss"],
})
export class AdministrationPageComponent implements OnInit, OnDestroy {
  public organizationDetails: IOrganizationDetails | null = null;
  public organizations: IExposedOrganizationOverview[] = [];

  private onDestroy$ = new Subject<void>();

  constructor(
    private readonly router: Router,
    private readonly organizationService: OrganizationService,
    private readonly permissionService: PermissionService,
    private readonly sessionService: SessionService,
    private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService
  ) {}

  /**
   * Loads every organization's overview for a SuperAdmin, or the current organization's own
   * details for an admin.
   */
  ngOnInit(): void {
    if (this.canViewAllOrganizations) {
      this.loadAllOrganizations();
    } else {
      this.loadOrganizationDetails();
    }
  }

  /**
   * Unsubscribes from all pending observables.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Whether the user administrates every organization (SuperAdmin) rather than only their own (admin)
   */
  get canViewAllOrganizations(): boolean {
    return this.organizationService.canGetAllOrganizations();
  }

  /**
   * Whether the user's role allows opening an organization's details page
   */
  get canViewOrganizationDetails(): boolean {
    return this.organizationService.canGetOrganizationDetailsById();
  }

  /**
   * Whether the user's role allows creating an organization
   */
  get canCreateOrganization(): boolean {
    return this.permissionService.isEnabled(Functionality.CREATE_ORGANIZATION);
  }

  /**
   * Whether the user's role allows inviting a member to the current organization
   */
  get canInviteMember(): boolean {
    return this.permissionService.isEnabled(Functionality.INVITE_MEMBER);
  }

  /**
   * @param overview Organization overview to check
   * @return Whether the given organization is the one the current session is scoped to
   */
  public isCurrentOrganization(overview: IExposedOrganizationOverview): boolean {
    return overview.organization.id === this.sessionService.currentOrgId;
  }

  /**
   * Navigates to the given organization's details page, provided the user's role allows opening it.
   * @param id The id of the organization to navigate to
   */
  public goToOrganization(id: number): void {
    if (!this.canViewOrganizationDetails) {
      return;
    }

    this.router.navigate(["/administration/organization", id]);
  }

  /**
   * Fetches and stores the current organization's details.
   */
  public loadOrganizationDetails(): void {
    this.organizationService
      .getOrganizationDetails()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (details) => {
          this.organizationDetails = details;
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
            duration: 5000,
          });
        },
      });
  }

  /**
   * Fetches and stores every organization's overview.
   */
  private loadAllOrganizations(): void {
    this.organizationService
      .getAllOrganizations()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (organizations) => {
          this.organizations = organizations;
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
            duration: 5000,
          });
        },
      });
  }
}
